import React, { createContext, useContext, useReducer, useState } from 'react';
import { RecordingRules, RoomType } from '../types';
import { TwilioError } from 'twilio-video';
import { settingsReducer, initialSettings, Settings, SettingsAction } from './settings/settingsReducer';
import useActiveSinkId from './useActiveSinkId/useActiveSinkId';
import useFirebaseAuth from './useFirebaseAuth/useFirebaseAuth';
import { useLocalStorageState } from '../hooks/useLocalStorageState/useLocalStorageState';
import usePasscodeAuth from './usePasscodeAuth/usePasscodeAuth';
import { User } from 'firebase/auth';

export interface StateContextType {
  error: TwilioError | Error | null;
  setError(error: TwilioError | Error | null): void;
  getToken(name: string, room: string, passcode?: string): Promise<{ room_type: RoomType; token: string }>;
  decodeAccessToken(token: string): Promise<{ payload: any }>;
  joinRoom(payload: any): Promise<{ payload: any }>;
  leaveRoom(payload: any): Promise<{ payload: any }>;
  getUserAggregateBds(payload: any): Promise<{ payload: any }>;
  user?: User | null | { displayName: undefined; photoURL: undefined; passcode?: string };
  signIn?(passcode?: string): Promise<void>;
  signOut?(): Promise<void>;
  isAuthReady?: boolean;
  isFetching: boolean;
  activeSinkId: string;
  setActiveSinkId(sinkId: string): void;
  settings: Settings;
  dispatchSetting: React.Dispatch<SettingsAction>;
  roomType?: RoomType;
  updateRecordingRules(room_sid: string, rules: RecordingRules): Promise<object>;
  isGalleryViewActive: boolean;
  setIsGalleryViewActive: React.Dispatch<React.SetStateAction<boolean>>;
  maxGalleryViewParticipants: number;
  setMaxGalleryViewParticipants: React.Dispatch<React.SetStateAction<number>>;
  isKrispEnabled: boolean;
  setIsKrispEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isKrispInstalled: boolean;
  setIsKrispInstalled: React.Dispatch<React.SetStateAction<boolean>>;
  tokenPayload?: any;
}

export const StateContext = createContext<StateContextType>(null!);

/*
  The 'react-hooks/rules-of-hooks' linting rules prevent React Hooks from being called
  inside of if() statements. This is because hooks must always be called in the same order
  every time a component is rendered. The 'react-hooks/rules-of-hooks' rule is disabled below
  because the "if (process.env.REACT_APP_SET_AUTH === 'firebase')" statements are evaluated
  at build time (not runtime). If the statement evaluates to false, then the code is not
  included in the bundle that is produced (due to tree-shaking). Thus, in this instance, it
  is ok to call hooks inside if() statements.
*/
export default function AppStateProvider(props: React.PropsWithChildren<{}>) {
  const [error, setError] = useState<TwilioError | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isGalleryViewActive, setIsGalleryViewActive] = useLocalStorageState('gallery-view-active-key', true);
  const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  const [settings, dispatchSetting] = useReducer(settingsReducer, initialSettings);
  const [roomType, setRoomType] = useState<RoomType>();
  const [tokenPayload, setTokenPayload] = useState<any>();
  const [maxGalleryViewParticipants, setMaxGalleryViewParticipants] = useLocalStorageState(
    'max-gallery-participants-key',
    6
  );

  const [isKrispEnabled, setIsKrispEnabled] = useState(false);
  const [isKrispInstalled, setIsKrispInstalled] = useState(false);

  let contextValue = {
    error,
    setError,
    isFetching,
    activeSinkId,
    setActiveSinkId,
    settings,
    dispatchSetting,
    roomType,
    isGalleryViewActive,
    setIsGalleryViewActive,
    maxGalleryViewParticipants,
    setMaxGalleryViewParticipants,
    isKrispEnabled,
    setIsKrispEnabled,
    isKrispInstalled,
    setIsKrispInstalled,
    tokenPayload,
  } as StateContextType;

  if (process.env.REACT_APP_SET_AUTH === 'firebase') {
    contextValue = {
      ...contextValue,
      ...useFirebaseAuth(), // eslint-disable-line react-hooks/rules-of-hooks
    };
  } else if (process.env.REACT_APP_SET_AUTH === 'passcode') {
    contextValue = {
      ...contextValue,
      ...usePasscodeAuth(), // eslint-disable-line react-hooks/rules-of-hooks
    };
  } else {
    contextValue = {
      ...contextValue,
      getToken: async (user_identity, room_name) => {
        const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT + '/getToken';
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            identity: user_identity,
            room_name,
            create_conversation: process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true',
          }),
        }).then(res => res.json());
      },
      getUserAggregateBds: async payload => {
        const endpoint = process.env.REACT_APP_BDS_URI + '/aggregateBds';
        const { userId } = payload;
        const requestData = {
          select: {
            index: {
              searchIndex: `${process.env.REACT_APP_ECOSYSTEM}:userNameIndex`,
              searchQuery: '*',
              searchOptions: 'TIMEOUT 10000',
            },
            list: {
              objectIds: [userId],
            },
            load: {
              profile: '$.persistent.static.live',
              serviceId: '$.serviceId',
            },
          },
          groupBy: {
            properties: ['@profile', '@serviceId'],
            reducers: [
              {
                func: 'TOLIST',
                arguments: ['@objectId'],
                name: 'objectIds',
              },
            ],
          },
          apply: {
            expression: '',
            name: '',
          },
          result: {
            pagination: {
              offset: 0,
              limit: 10,
            },
          },
          filter: {
            expression: '',
          },
          anyStatus: false,
        };
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(requestData),
        }).then(res => res.json());
      },
      decodeAccessToken: async payload => {
        const endpoint = process.env.REACT_APP_API_URI + '/decryptPayloadTopic';
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ payload }),
        }).then(res => res.json());
      },
      joinRoom: async payload => {
        const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT + '/joinRoom';
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        }).then(res => res.json());
      },
      leaveRoom: async payload => {
        const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT + '/leaveRoom';
        return fetch(endpoint, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify(payload),
        }).then(res => res.json());
      },
      updateRecordingRules: async (room_sid, rules) => {
        const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

        return fetch(endpoint, {
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ room_sid, rules }),
          method: 'POST',
        })
          .then(async res => {
            const jsonResponse = await res.json();

            if (!res.ok) {
              const recordingError = new Error(
                jsonResponse.error?.message || 'There was an error updating recording rules'
              );
              recordingError.code = jsonResponse.error?.code;
              return Promise.reject(recordingError);
            }

            return jsonResponse;
          })
          .catch(err => setError(err));
      },
    };
  }

  const getToken: StateContextType['getToken'] = (name, room) => {
    setIsFetching(true);
    return contextValue
      .getToken(name, room)
      .then((res: any) => {
        setRoomType('group');
        setIsFetching(false);
        return { token: res?.data, room_type: 'group' as RoomType };
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const decodeAccessToken: StateContextType['decodeAccessToken'] = token => {
    setIsFetching(true);
    return contextValue
      .decodeAccessToken(token)
      .then((res: any) => {
        setIsFetching(false);
        const payload = JSON.parse(res?.data?.decryptedPayload);
        setTokenPayload(payload);
        return payload;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const getUserAggregateBds: StateContextType['getUserAggregateBds'] = payload => {
    return contextValue
      .getUserAggregateBds(payload)
      .then((response: any) => {
        let user: any = {
          objectId: undefined,
          profile: undefined,
          email: undefined,
          phone: undefined,
          profileImage: undefined,
          beamId: undefined,
          roleId: undefined,
          department: undefined,
          stripeCustomerId: undefined,
          stripePaymentMethods: undefined
        };
        response?.data?.map((x: any) => {
          user.objectId = x.objectIds?.toString();
          if(x.profile?.alternativeIds?.email){
            user.email = x.profile?.alternativeIds?.email
          }
          if(x.profile?.alternativeIds?.phone){
            user.phone = x.profile?.alternativeIds?.phone
          }
          if(x.profile?.alternativeIds?.stripeCustomerId){
            user.stripeCustomerId = x.profile?.alternativeIds?.stripeCustomerId
          }
          if(x.profile?.profile?.stripePaymentMethods){
            user.stripePaymentMethods = x.profile?.profile?.stripePaymentMethods
          }
          if(x.profile?.profile?.firstName || x.profile?.profile?.firstName){
            user.profile = x.profile?.profile
          }
          if(x.profile?.beamIds?.[0]){
            user.beamId = x.profile?.beamIds?.[0]
          }
          if (x.profile?.profile?.profileImage?.urls?.original?.url) {
            user.profileImage = x.profile?.profile?.profileImage.urls?.original?.url;
          }
        });
        user = {
          ...user,
        };
        return user;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const joinRoom: StateContextType['joinRoom'] = payload => {
    setIsFetching(true);
    return contextValue
      .joinRoom(payload)
      .then((res: any) => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const leaveRoom: StateContextType['leaveRoom'] = payload => {
    setIsFetching(true);
    return contextValue
      .leaveRoom(payload)
      .then((res: any) => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const updateRecordingRules: StateContextType['updateRecordingRules'] = (room_sid, rules) => {
    setIsFetching(true);
    return contextValue
      .updateRecordingRules(room_sid, rules)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  return (
    <StateContext.Provider
      value={{
        ...contextValue,
        getToken,
        updateRecordingRules,
        decodeAccessToken,
        joinRoom,
        leaveRoom,
        getUserAggregateBds,
      }}
    >
      {props.children}
    </StateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
